import React, { useState, useEffect } from 'react';
import { getExpenseSummaryApi } from '../../../Apis/Api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';
import toast from 'react-hot-toast';

const ExpenseSummary = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [customRange, setCustomRange] = useState({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  });

  useEffect(() => {
    fetchSummary();
  }, [timeRange, customRange]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      let params = { period: timeRange }; // Changed from timeRange to period to match backend

      if (timeRange === 'custom') {
        params.startDate = customRange.startDate;
        params.endDate = customRange.endDate;
      }

      const response = await getExpenseSummaryApi(params);
      setSummaryData(response.data || null); // Changed to expect an object
    } catch (error) {
      toast.error('Failed to fetch expense summary');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setCustomRange({
      ...customRange,
      [name]: value
    });
  };

  // Prepare chart data from categories array
  const chartData = summaryData?.categories?.map(item => ({
    name: item.category,
    amount: item.totalAmount
  })) || [];

  const totalExpenses = summaryData?.totalExpenses || 0;

  return (
    <div className="mt-5">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h2 className="text-gray-600 font-bold text-2xl mb-4 md:mb-0">Expense Summary</h2>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleTimeRangeChange('day')}
              className={`px-4 py-2 rounded-md ${timeRange === 'day' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Today
            </button>
            <button
              onClick={() => handleTimeRangeChange('month')}
              className={`px-4 py-2 rounded-md ${timeRange === 'month' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              This Month
            </button>
            <button
              onClick={() => handleTimeRangeChange('year')}
              className={`px-4 py-2 rounded-md ${timeRange === 'year' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              This Year
            </button>
            {/* <button
              onClick={() => handleTimeRangeChange('custom')}
              className={`px-4 py-2 rounded-md ${timeRange === 'custom' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Custom
            </button> */}
          </div>
        </div>

        {timeRange === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={customRange.startDate}
                onChange={handleDateChange}
                className="w-full p-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                name="endDate"
                value={customRange.endDate}
                onChange={handleDateChange}
                className="w-full p-2 border rounded-md"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : summaryData ? (
          <>
            <div className="mb-4">
              <p className="text-gray-600">
                <span className="font-semibold">Period:</span> {moment(summaryData.startDate).format('MMM D, YYYY')} - {moment(summaryData.endDate).format('MMM D, YYYY')}
              </p>
            </div>

            <div className="mb-8 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`NPR ${value}`, 'Amount']} />
                  <Legend />
                  <Bar dataKey="amount" name="Expense Amount" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Category</th>
                    <th className="py-3 px-6 text-right">Amount (NPR)</th>
                    <th className="py-3 px-6 text-right">Percentage</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm">
                  {summaryData.categories?.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4">No expense data found</td>
                    </tr>
                  ) : (
                    summaryData.categories?.map((item) => (
                      <tr key={item.category} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-6 text-left capitalize">{item.category.replace(/_/g, ' ')}</td>
                        <td className="py-3 px-6 text-right">{item.totalAmount.toLocaleString()}</td>
                        <td className="py-3 px-6 text-right">
                          {totalExpenses > 0 ? ((item.totalAmount / totalExpenses) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td className="py-3 px-6 text-right">Total:</td>
                    <td className="py-3 px-6 text-right">{totalExpenses.toLocaleString()}</td>
                    <td className="py-3 px-6 text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-4">No summary data available</div>
        )}
      </div>
    </div>
  );
};

export default ExpenseSummary;